// Camada de dados do admin: sessão (JWT do login) + cliente tipado do SDK.
import { createBBuzzClient, type BBuzzClient } from '@bzapper/sdk';
import { QueryClient } from '@tanstack/react-query';

const TOKEN = 'bz_token';
const BASE = 'bz_api_base';
const PROJECT = 'bz_project';

/** Projeto ativo (enviado como X-Project-Id em toda chamada). */
export function getProject(): string {
  return localStorage.getItem(PROJECT) ?? '';
}
export function setProject(id: string) {
  if (id) localStorage.setItem(PROJECT, id);
  else localStorage.removeItem(PROJECT);
}
function projectHeaders(): Record<string, string> {
  const p = getProject();
  return p ? { 'X-Project-Id': p } : {};
}

export const DEFAULT_BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:8080';

export const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 15_000 } },
});

export function getToken(): string | null {
  return localStorage.getItem(TOKEN);
}
// Quem manda é a base do build; a salva é só cache. O contrário trancava o app
// PARA SEMPRE: uma base velha no localStorage (a porta de ontem, o domínio de
// outro ambiente) vencia o .env, e como toda tentativa de login regravava esse
// mesmo valor, o veneno se perpetuava. Trocar o .env, refazer o build e o
// recarregamento forçado não adiantavam nada, e clearSession não removia a
// chave — a única saída era abrir o DevTools e limpar na mão, coisa que o
// cliente não faz. Para ele o bZapper só parava de funcionar, sem erro nenhum.
export function apiBase(): string {
  const saved = localStorage.getItem(BASE);
  if (saved !== null && saved !== DEFAULT_BASE) {
    localStorage.removeItem(BASE);
    return DEFAULT_BASE;
  }
  return saved ?? DEFAULT_BASE;
}
export function setSession(token: string, baseUrl = apiBase()) {
  localStorage.setItem(TOKEN, token);
  localStorage.setItem(BASE, baseUrl);
}
// Sair da conta também descarta a base: é a saída de emergência de quem ficou
// preso apontado para um endereço que não responde mais.
export function clearSession() {
  localStorage.removeItem(TOKEN);
  localStorage.removeItem(BASE);
}

/**
 * tokenValid diz se o JWT da sessão ainda está dentro da validade, olhando o `exp` do
 * próprio token. É uma checagem LOCAL e barata — quem manda continua sendo o servidor.
 *
 * Existe porque o guard de rota só perguntava "tem token?". Um JWT vencido (a sessão
 * dura 7 dias) continuava no localStorage, o guard deixava passar, e o painel abria
 * inteiro com todas as chamadas em 401: telas vazias, plano "Free", nenhum número.
 * Quem via isso achava que tinha perdido a conta.
 *
 * Token opaco (sem 3 partes ou sem `exp`) é tratado como válido: aí o 401 resolve.
 */
export function tokenValid(token: string | null = getToken()): boolean {
  if (!token) return false;
  const parts = token.split('.');
  if (parts.length !== 3) return true;
  try {
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    const exp = (JSON.parse(json) as { exp?: number }).exp;
    return typeof exp === 'number' ? exp * 1000 > Date.now() : true;
  } catch {
    return true;
  }
}

/** Rotas públicas: um 401 ali é credencial errada, não sessão morta. */
function onAuthScreen(): boolean {
  return /^\/(login|signup|verify|forgot|reset)\b/.test(window.location.pathname);
}

let redirecting = false;

/**
 * onUnauthorized encerra a sessão e manda para o login, uma vez só.
 *
 * Cobre o que o `exp` não pega: token ainda no prazo mas recusado pelo servidor
 * (usuário removido, segredo trocado, key revogada). Sem isso o painel ficava de pé,
 * mudo, com tudo zerado. Guarda o caminho atual em `?next=` para voltar depois do login.
 */
export function onUnauthorized() {
  if (redirecting || onAuthScreen() || !getToken()) return;
  redirecting = true;
  clearSession();
  const atual = window.location.pathname + window.location.search;
  const destino = atual && atual !== '/' ? `/login?next=${encodeURIComponent(atual)}` : '/login';
  // replace: o botão "voltar" não devolve o usuário ao painel morto.
  window.location.replace(destino);
}

/** fetch que reage ao 401 (sessão expirada/inválida) e segue transparente no resto. */
export async function sessionFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const res = await fetch(input, init);
  if (res.status === 401) onUnauthorized();
  return res;
}

export function makeClient(locale = 'pt-BR'): BBuzzClient {
  return createBBuzzClient({
    baseUrl: apiBase(), apiKey: getToken() ?? undefined, locale, headers: projectHeaders(),
    fetch: sessionFetch,
  });
}

export interface RawResult {
  status: number;
  ok: boolean;
  durationMs: number;
  data: unknown;
}

/** Requisição genérica autenticada (usada pelo Playground). Nunca lança: devolve status+corpo. */
export async function apiFetch(method: string, path: string, body?: unknown): Promise<RawResult> {
  const started = performance.now();
  const headers: Record<string, string> = { Authorization: `Bearer ${getToken() ?? ''}`, ...projectHeaders() };
  // FormData (upload): NÃO define Content-Type — o browser põe o boundary do multipart.
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
  let status = 0;
  let data: unknown = null;
  try {
    const res = await sessionFetch(apiBase() + path, {
      method,
      headers,
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
    });
    status = res.status;
    const text = await res.text();
    try { data = text ? JSON.parse(text) : null; } catch { data = text; }
    return { status, ok: res.ok, durationMs: Math.round(performance.now() - started), data };
  } catch (e) {
    return { status: 0, ok: false, durationMs: Math.round(performance.now() - started), data: { error: String(e) } };
  }
}

/**
 * downloadFile baixa um arquivo de uma rota AUTENTICADA (ex.: /contacts/export).
 *
 * Não dá para usar <a href> direto: a rota exige Authorization + X-Project-Id, e
 * link nenhum manda header. Então buscamos o corpo, guardamos num blob e
 * clicamos num link temporário. O nome respeita o `Content-Disposition` do
 * servidor (é ele quem carimba a data no arquivo); `fallbackName` só entra se o
 * header vier ausente ou ilegível.
 */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const res = await sessionFetch(apiBase() + path, {
    headers: { Authorization: `Bearer ${getToken() ?? ''}`, ...projectHeaders() },
  });
  if (!res.ok) {
    let code = 'download_failed';
    try { code = ((await res.json()) as { code?: string }).code ?? code; } catch { /* corpo não-JSON */ }
    throw new Error(code);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filenameFrom(res.headers.get('Content-Disposition')) ?? fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // O objeto só é liberado depois do clique: revogar antes cancela o download.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Extrai o filename do Content-Disposition (aceita a forma RFC 5987 `filename*`). */
function filenameFrom(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star) { try { return decodeURIComponent(star[1].trim().replace(/^"|"$/g, '')); } catch { /* segue */ } }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : null;
}

/** Upload do avatar (multipart) — devolve a URL pública ou lança com o código do erro. */
export async function uploadAvatar(file: File): Promise<string> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(apiBase() + '/me/avatar', {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken() ?? ''}`, ...projectHeaders() },
    body: fd,
  });
  if (!res.ok) {
    let code = 'upload_failed';
    try { code = (await res.json()).code ?? code; } catch { /* ignore */ }
    throw new Error(code);
  }
  return ((await res.json()) as { avatar_url: string }).avatar_url;
}

/** Upload do logo da marca (multipart) — devolve a URL pública ou lança com o código. */
export async function uploadBrandLogo(file: File): Promise<string> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(apiBase() + '/brand/logo', {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken() ?? ''}`, ...projectHeaders() },
    body: fd,
  });
  if (!res.ok) {
    let code = 'upload_failed';
    try { code = (await res.json()).code ?? code; } catch { /* ignore */ }
    throw new Error(code);
  }
  return ((await res.json()) as { logo_url: string }).logo_url;
}

/** Upload do logo do PROJETO (multipart) — devolve a URL pública ou lança com o código. */
export async function uploadProjectLogo(projectId: string, file: File): Promise<string> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(apiBase() + `/projects/${projectId}/logo`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken() ?? ''}`, ...projectHeaders() },
    body: fd,
  });
  if (!res.ok) {
    let code = 'upload_failed';
    try { code = (await res.json()).code ?? code; } catch { /* ignore */ }
    throw new Error(code);
  }
  return ((await res.json()) as { logo_url: string }).logo_url;
}

/** URL do stream SSE (token via query — EventSource não envia headers). */
export function streamUrl(instanceId?: string): string {
  const u = new URL(apiBase() + '/stream');
  const tok = getToken();
  if (tok) u.searchParams.set('access_token', tok);
  if (instanceId) u.searchParams.set('instance_id', instanceId);
  return u.toString();
}

/** URL do SSE de EVENTOS DE WEBHOOK do projeto ativo (inspector ao vivo). */
export function webhookListenUrl(): string {
  const u = new URL(apiBase() + '/webhooks/listen');
  const tok = getToken();
  if (tok) u.searchParams.set('access_token', tok);
  const p = getProject();
  if (p) u.searchParams.set('project_id', p); // EventSource não envia X-Project-Id
  return u.toString();
}
